import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #searchRefinementsDebugFrame = null;

  static get observedAttributes() {
    return [
      'data-gsc-search-refinements-enabled',
      'data-gsc-search-refinements-has-menu',
      'data-gsc-search-performed',
      'data-gsc-search-results-count',
      'data-gsc-search-filters-count',
      'data-gsc-search-refinements-will-render',
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#logSearchRefinementsDebug();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected) return;
    this.#scheduleSearchRefinementsDebugLog();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #logSearchRefinementsDebug() {
    if (!this.hasAttribute('data-gsc-search-refinements-enabled')) return;

    const enabled = this.dataset.gscSearchRefinementsEnabled === 'true';
    const performed = this.dataset.gscSearchPerformed === 'true';
    const resultsCount = Number(this.dataset.gscSearchResultsCount || 0);
    const filtersCount = Number(this.dataset.gscSearchFiltersCount || 0);
    const willRender = this.dataset.gscSearchRefinementsWillRender === 'true';
    let reason = 'will_render';

    if (!enabled) {
      reason = 'disabled';
    } else if (!performed) {
      reason = 'search_not_performed';
    } else if (resultsCount <= 1000) {
      reason = 'results_count_at_or_below_1000';
    } else if (filtersCount > 0) {
      reason = 'shopify_filters_available';
    } else if (!willRender) {
      reason = 'unknown_not_rendering';
    }

    window.GSCSearchRefinementsDebug = {
      enabled,
      hasRefinementMenu: this.dataset.gscSearchRefinementsHasMenu === 'true',
      performed,
      resultsCount,
      filtersCount,
      willRender,
      reason,
    };

    console.log('GSC search refinements', window.GSCSearchRefinementsDebug);
  }

  #scheduleSearchRefinementsDebugLog() {
    if (this.#searchRefinementsDebugFrame) return;

    this.#searchRefinementsDebugFrame = requestAnimationFrame(() => {
      this.#searchRefinementsDebugFrame = null;
      this.#logSearchRefinementsDebug();
    });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
